import type { TreeTypeGrade, GradeDiscount } from '../types/nursery';

/**
 * A tree type's grades, as edited: best first, each with the share of a crop
 * expected at it before grading. The server checks the same rules
 * (HHNodeServer nurseryGradeService.gradeSetProblem).
 */
export type GradeDraft = Pick<TreeTypeGrade, 'name' | 'description' | 'expected_share_pct'>;

export function gradesTotal(rows: GradeDraft[]): number {
  return Math.round(rows.reduce((sum, g) => sum + (Number(g.expected_share_pct) || 0), 0) * 100) / 100;
}

/** What stops the grades saving, or null. No grades is fine: an ungraded tree type. */
export function gradesProblem(rows: GradeDraft[]): string | null {
  if (!rows.length) return null;
  if (rows.some((g) => !g.name.trim())) return 'Every grade needs a name';
  const names = new Set(rows.map((g) => g.name.trim().toLowerCase()));
  if (names.size !== rows.length) return 'Two grades have the same name';
  const total = gradesTotal(rows);
  if (Math.abs(total - 100) > 0.01) return `The expected shares add up to ${total}%, not 100%`;
  return null;
}

/** Rows as the server takes them: numbered 1, 2, 3… in the order shown. */
export function gradesPayload(rows: GradeDraft[]) {
  return rows.map((g, i) => ({
    grade_rank: i + 1,
    name: g.name.trim(),
    description: g.description?.trim() || null,
    expected_share_pct: Number(g.expected_share_pct) || 0,
  }));
}

export function sameGrades(a: GradeDraft[], b: GradeDraft[]): boolean {
  return JSON.stringify(gradesPayload(a)) === JSON.stringify(gradesPayload(b));
}

/**
 * A lower grade's price: the 1st grade's less the organisation's % for that
 * grade. Mirrors HHNodeServer nurseryGradeService.gradePrice.
 */
export function gradePrice(firstGradePrice: number, gradeRank: number, discounts: GradeDiscount[]): number {
  if (gradeRank <= 1) return firstGradePrice;
  const pct = discounts.find((d) => d.grade_rank === gradeRank)?.discount_pct ?? 0;
  return Math.round(firstGradePrice * (100 - pct)) / 100;
}

export const GRADE_ORDINAL = ['1st', '2nd', '3rd', '4th', '5th', '6th', '7th', '8th', '9th'];
